/**
 * Customer Service screen — admin.nurev.io's most-used surface.
 *
 * Ports the legacy CustomerService view from app.nurev.io (the publisher
 * dashboard) into the new admin console, with two notable cleanups:
 *
 *   1. The legacy code referenced `overview.creators` and
 *      `results.creators` in places where the API actually returns
 *      `publishers`. That made the publisher columns silently
 *      render empty on the old dashboard. Fixed here.
 *   2. Legacy referenced `publisher.publisherKey` in a scope where
 *      that symbol didn't exist (would have thrown at runtime if
 *      the path were ever hit). Fixed to use `creator.publisherKey`.
 *
 * The three tabs (overview / search / detail) coexist in one screen
 * for the same reason the legacy did: a customer-service rep is
 * typically searching → drilling → adjusting → returning to search,
 * and the back button needs to land them where they left off.
 */
import { FormEvent, useCallback, useMemo, useState } from 'react';
import { Search as SearchIcon, AlertCircle, ExternalLink } from 'lucide-react';
import {
  useAdjustBalance,
  useSupportDevice,
  useSupportOverview,
  useSupportPublisher,
  useSupportSearch
} from '@/hooks/useAdminSupport';
import {
  AppSummary,
  DeviceSummary,
  PublisherSummary,
  SessionDetail,
  WithdrawalSummary
} from '@/api/adminSupport';
import {
  BackButton,
  Badge,
  Card,
  DetailCell,
  Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import {
  fmtDate,
  fmtDateTime,
  fmtDuration,
  fmtNum
} from '@/lib/format';

const platformGlyph: Record<string, string> = {
  windows: 'Win',
  mac: 'Mac',
  linux: 'Linux'
};

// ─── Overview Tab ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading, error } = useSupportOverview();

  if (error) {
    return (
      <Card className="text-center">
        <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
        <p className="text-sm text-nurev-muted">
          Couldn't load overview stats. Re-login if this persists.
        </p>
      </Card>
    );
  }

  const pubs = data?.publishers || { total: 0, active: 0, pending: 0 };
  const devs = data?.devices || { total: 0, installed: 0 };
  const platforms = data?.platforms || {};
  const sources = data?.sources || {};
  const trend = data?.installTrend || [];
  const maxTrendCount = useMemo(
    () => trend.reduce((m, d) => Math.max(m, d.count), 0),
    [trend]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Publishers"
          value={fmtNum(pubs.total)}
          sub={`${pubs.active || 0} active · ${pubs.pending || 0} pending`}
          loading={isLoading}
        />
        <StatCard
          label="Total Devices"
          value={fmtNum(devs.total)}
          sub={`${devs.installed || 0} installed`}
          loading={isLoading}
        />
        <StatCard
          label="Active Sessions"
          value={fmtNum(data?.activeSessions)}
          color="text-emerald-300"
          loading={isLoading}
        />
        <StatCard
          label="Platforms"
          value={fmtNum(
            (platforms.windows || 0) + (platforms.mac || 0) + (platforms.linux || 0)
          )}
          sub={`Win ${platforms.windows || 0} / Mac ${platforms.mac || 0} / Linux ${
            platforms.linux || 0
          }`}
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Source Breakdown</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : Object.keys(sources).length === 0 ? (
            <p className="text-sm text-nurev-muted">No data yet.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(sources).map(([key, count]) => (
                <div key={key} className="rounded-lg bg-white/[0.03] p-3">
                  <p className="text-xs capitalize text-nurev-muted">{key}</p>
                  <p className="text-lg font-bold text-nurev-text">
                    {fmtNum(count)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <SectionLabel>7-Day Installs</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : trend.length === 0 ? (
            <p className="text-sm text-nurev-muted">
              No installs in the last 7 days.
            </p>
          ) : (
            <div className="flex h-20 items-end gap-1">
              {trend.map(d => {
                const pct = maxTrendCount > 0 ? (d.count / maxTrendCount) * 100 : 0;
                return (
                  <div key={d._id} className="flex flex-1 flex-col items-center gap-1">
                    <span className="text-[9px] text-nurev-muted">{d.count}</span>
                    <div
                      className="w-full rounded-t bg-amber-500/60"
                      style={{ height: `${Math.max(4, pct)}%` }}
                    />
                    <span className="text-[8px] text-nurev-muted">
                      {d._id.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Search Tab ───────────────────────────────────────────────────────────

interface SearchTabProps {
  onSelectDevice: (id: string) => void;
  onSelectPublisher: (id: string) => void;
}

function SearchTab({ onSelectDevice, onSelectPublisher }: SearchTabProps) {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState<'all' | 'publishers' | 'devices'>(
    'all'
  );
  const [page, setPage] = useState(1);
  const [submitted, setSubmitted] = useState('');

  const params = useMemo(
    () => ({ q: submitted, type: searchType, page, limit: 20 }),
    [submitted, searchType, page]
  );
  const { data, isLoading, isFetching } = useSupportSearch(
    params,
    Boolean(submitted)
  );

  const handleSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setSubmitted(query.trim());
      setPage(1);
    },
    [query]
  );

  const publishers = data?.publishers;
  const devices = data?.devices;
  const hasResults =
    (publishers?.data?.length || 0) + (devices?.data?.length || 0) > 0;

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-nurev-muted" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, email, user key, device, publisher key..."
            className="w-full rounded-[10px] border border-nurev-border bg-white/[0.04] py-2.5 pl-9 pr-3 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
          />
        </div>
        <select
          value={searchType}
          onChange={e =>
            setSearchType(e.target.value as 'all' | 'publishers' | 'devices')
          }
          className="rounded-[10px] border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        >
          <option value="all">All</option>
          <option value="publishers">Publishers</option>
          <option value="devices">Devices</option>
        </select>
        <button
          type="submit"
          disabled={!query || query.trim().length < 2}
          className="rounded-[10px] bg-amber-500 px-5 py-2.5 text-sm font-medium text-nurev-bg transition hover:bg-amber-400 disabled:opacity-30"
        >
          Search
        </button>
      </form>

      {submitted && isFetching && (
        <div className="py-2 text-center text-xs text-nurev-muted">Searching…</div>
      )}

      {publishers && publishers.data.length > 0 && (
        <Card>
          <SectionLabel>Publishers ({publishers.total})</SectionLabel>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 text-right font-medium">Installs</th>
                  <th className="pb-2 text-right font-medium">Live</th>
                  <th className="pb-2 font-medium">Joined</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-nurev-border">
                {publishers.data.map(p => (
                  <tr key={p._id}>
                    <td className="py-2">
                      <p className="font-medium text-nurev-text">
                        {p.name || p.companyName || '—'}
                      </p>
                      {p.publisherNumber ? (
                        <p className="font-mono text-[10px] text-amber-300">
                          #{p.publisherNumber}
                        </p>
                      ) : null}
                      {p.companyName && p.name && (
                        <p className="text-[10px] text-nurev-muted">{p.companyName}</p>
                      )}
                    </td>
                    <td className="py-2 text-xs text-nurev-muted">{p.email}</td>
                    <td className="py-2">
                      <Badge value={p.status} />
                    </td>
                    <td className="py-2 text-right font-mono text-nurev-text">
                      {fmtNum(p.installs)}
                    </td>
                    <td className="py-2 text-right font-mono text-nurev-text">
                      {fmtNum(p.live)}
                    </td>
                    <td className="py-2 text-xs text-nurev-muted">
                      {fmtDate(p.createdAt)}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => onSelectPublisher(p._id)}
                        className="text-[10px] text-amber-300 hover:text-amber-200"
                      >
                        view
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {publishers.pages > 1 && (
            <Pagination page={page} totalPages={publishers.pages} onChange={setPage} />
          )}
        </Card>
      )}

      {devices && devices.data.length > 0 && (
        <Card>
          <SectionLabel>Devices ({devices.total})</SectionLabel>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                  <th className="pb-2 font-medium">User Key</th>
                  <th className="pb-2 font-medium">Device</th>
                  <th className="pb-2 font-medium">Platform</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Publisher</th>
                  <th className="pb-2 text-right font-medium">Balance</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-nurev-border">
                {devices.data.map(d => (
                  <tr key={d._id}>
                    <td className="max-w-[140px] truncate py-2 font-mono text-xs text-nurev-text">
                      {d.userKey}
                    </td>
                    <td className="py-2">
                      <p className="text-xs text-nurev-text">{d.device || '—'}</p>
                      <p className="text-[10px] text-nurev-muted">
                        {d.operatingSystem}
                      </p>
                    </td>
                    <td className="py-2 text-xs">
                      {d.platform ? platformGlyph[d.platform] || d.platform : '—'}
                    </td>
                    <td className="py-2">
                      <Badge value={d.status} />
                    </td>
                    <td className="py-2">
                      <p className="text-xs text-nurev-text">
                        {d.publisherId?.name || '—'}
                      </p>
                      <p className="text-[10px] text-nurev-muted">
                        {d.publisherId?.email}
                      </p>
                    </td>
                    <td className="py-2 text-right font-mono text-xs text-nurev-text">
                      {fmtNum(Math.round((d.balance || 0) / 10))}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => onSelectDevice(d._id)}
                        className="text-[10px] text-amber-300 hover:text-amber-200"
                      >
                        view
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {devices.pages > 1 && (
            <Pagination page={page} totalPages={devices.pages} onChange={setPage} />
          )}
        </Card>
      )}

      {submitted && !isLoading && !hasResults && (
        <Card className="p-10 text-center">
          <p className="text-nurev-muted">No results for "{submitted}"</p>
        </Card>
      )}

      {!submitted && (
        <Card className="p-10 text-center text-sm text-nurev-muted">
          Start typing to search publishers and devices. Min 2 chars.
        </Card>
      )}
    </div>
  );
}

// ─── Device Detail ────────────────────────────────────────────────────────

interface DeviceDetailProps {
  deviceId: string;
  onBack: () => void;
  onSelectPublisher: (id: string) => void;
}

function DeviceDetailPanel({
  deviceId,
  onBack,
  onSelectPublisher
}: DeviceDetailProps) {
  const { data, isLoading, error } = useSupportDevice(deviceId);
  const adjustMutation = useAdjustBalance();
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustErr, setAdjustErr] = useState<string | null>(null);
  const [adjustOk, setAdjustOk] = useState<string | null>(null);

  const device = data?.device;
  const sessions: SessionDetail[] = data?.sessions || [];
  const activeSession = data?.activeSession;

  const handleAdjust = async (e: FormEvent) => {
    e.preventDefault();
    setAdjustErr(null);
    setAdjustOk(null);
    const amt = parseFloat(adjustAmount);
    if (!Number.isFinite(amt) || !adjustReason.trim()) {
      setAdjustErr('Amount and reason are required.');
      return;
    }
    try {
      const res = await adjustMutation.mutateAsync({
        deviceId,
        amount: amt,
        reason: adjustReason.trim()
      });
      setAdjustOk(
        `New balance: ${fmtNum(Math.round(res.newBalance))} (was ${fmtNum(
          Math.round((device?.balanceDisplay || 0) - amt)
        )})`
      );
      setAdjustAmount('');
      setAdjustReason('');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to adjust balance';
      setAdjustErr(msg);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label="Back to search" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label="Back to search" />
        <Card className="text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
          <p className="text-sm text-nurev-muted">Device not found.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackButton onClick={onBack} label="Back to search" />

      <Card>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-bold text-nurev-text">
              {device.device || 'Unknown Device'}
            </p>
            <p className="font-mono text-xs text-nurev-muted">{device.userKey}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge value={device.status} />
            {activeSession && (
              <span className="text-[10px] text-emerald-300">● online</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <DetailCell label="Platform">
            {device.platform ? platformGlyph[device.platform] || device.platform : '—'} /{' '}
            {device.operatingSystem || '—'}
          </DetailCell>
          <DetailCell label="Balance" mono>
            {fmtNum(Math.round(device.balanceDisplay || 0))}
          </DetailCell>
          <DetailCell label="Total Time">
            {fmtDuration(device.liveTime)}
          </DetailCell>
          <DetailCell label="Earned / Spent">
            {fmtNum(device.currencyEarned)} / {fmtNum(device.currencySpent)}
          </DetailCell>
          <DetailCell label="Source" capitalize>
            {device.source || 'unknown'}
          </DetailCell>
          <DetailCell label="Installed">
            {fmtDateTime(device.installedAt)}
          </DetailCell>
          <DetailCell label="Publisher">
            {device.publisherId ? (
              <button
                onClick={() => onSelectPublisher(device.publisherId!._id)}
                className="text-amber-300 hover:text-amber-200"
              >
                {device.publisherId.name || device.publisherId.email || '—'}
              </button>
            ) : (
              '—'
            )}
          </DetailCell>
          <DetailCell label="App">{device.appId?.name || '—'}</DetailCell>
        </div>
      </Card>

      <Card>
        <SectionLabel>Adjust Balance</SectionLabel>
        <form onSubmit={handleAdjust} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-shrink-0">
            <label className="mb-1 block text-[10px] text-nurev-muted">
              Amount (+/−)
            </label>
            <input
              type="number"
              step="1"
              value={adjustAmount}
              onChange={e => setAdjustAmount(e.target.value)}
              placeholder="100 or -50"
              className="w-32 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 font-mono text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-[10px] text-nurev-muted">
              Reason (audit-logged)
            </label>
            <input
              type="text"
              value={adjustReason}
              onChange={e => setAdjustReason(e.target.value)}
              placeholder="Customer support ticket #..."
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={adjustMutation.isPending || !adjustAmount || !adjustReason}
            className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-nurev-bg transition hover:bg-amber-400 disabled:opacity-30"
          >
            {adjustMutation.isPending ? '…' : 'Adjust'}
          </button>
        </form>
        {adjustErr && (
          <p className="mt-2 text-xs text-red-300">{adjustErr}</p>
        )}
        {adjustOk && (
          <p className="mt-2 text-xs text-emerald-300">{adjustOk}</p>
        )}
      </Card>

      <Card>
        <SectionLabel>Recent Sessions ({sessions.length})</SectionLabel>
        {sessions.length === 0 ? (
          <p className="text-sm text-nurev-muted">No sessions recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                  <th className="pb-2 font-medium">Started</th>
                  <th className="pb-2 font-medium">Ended</th>
                  <th className="pb-2 font-medium">Duration</th>
                  <th className="pb-2 font-medium">Platform</th>
                  <th className="pb-2 font-medium">Source</th>
                  <th className="pb-2 text-right font-medium">Speed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nurev-border">
                {sessions.map(s => (
                  <tr key={s._id} className={!s.endAt ? 'bg-emerald-500/5' : ''}>
                    <td className="py-2 text-xs text-nurev-text">
                      {fmtDateTime(s.startAt)}
                    </td>
                    <td className="py-2 text-xs">
                      {s.endAt ? (
                        <span className="text-nurev-muted">
                          {fmtDateTime(s.endAt)}
                        </span>
                      ) : (
                        <span className="font-medium text-emerald-300">Active</span>
                      )}
                    </td>
                    <td className="py-2 text-xs text-nurev-text">
                      {fmtDuration(s.duration)}
                    </td>
                    <td className="py-2 text-xs">
                      {s.platform ? platformGlyph[s.platform] || s.platform : '—'}
                    </td>
                    <td className="py-2 text-xs capitalize text-nurev-muted">
                      {s.source || '—'}
                    </td>
                    <td className="py-2 text-right font-mono text-xs text-nurev-text">
                      {s.minerStats?.hashrate ? fmtNum(s.minerStats.hashrate) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Publisher Detail ─────────────────────────────────────────────────────

interface PublisherDetailProps {
  publisherId: string;
  onBack: () => void;
}

function PublisherDetailPanel({ publisherId, onBack }: PublisherDetailProps) {
  const { data, isLoading, error } = useSupportPublisher(publisherId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label="Back to search" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label="Back to search" />
        <Card className="text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
          <p className="text-sm text-nurev-muted">Publisher not found.</p>
        </Card>
      </div>
    );
  }

  const { publisher, apps, balance, devices, activeSessions, recentWithdrawals } = data;

  return (
    <div className="space-y-4">
      <BackButton onClick={onBack} label="Back to search" />

      <Card>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-lg font-bold text-nurev-text">
              {publisher.name || publisher.companyName || '—'}
            </p>
            <p className="text-xs text-nurev-muted">{publisher.email}</p>
          </div>
          <Badge value={publisher.status} />
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <DetailCell label="Publisher #" mono>
            {publisher.publisherNumber ? `#${publisher.publisherNumber}` : '—'}
          </DetailCell>
          <DetailCell label="Role" capitalize>
            {publisher.role || '—'}
          </DetailCell>
          <DetailCell label="Balance (USD)" mono>
            {fmtNum(balance)}
          </DetailCell>
          <DetailCell label="Devices">
            {devices?.installed || 0} installed / {devices?.total || 0} total
          </DetailCell>
          <DetailCell label="Active Sessions" color="text-emerald-300">
            {activeSessions || 0}
          </DetailCell>
          <DetailCell label="Company">{publisher.companyName || '—'}</DetailCell>
          <DetailCell label="Publisher Key" mono>
            {publisher.publisherKey || '—'}
          </DetailCell>
          <DetailCell label="Payout" upper>
            {publisher.payoutCurrency || 'xmr'}
          </DetailCell>
          <DetailCell label="Joined">{fmtDate(publisher.createdAt)}</DetailCell>
        </div>
      </Card>

      {apps.length > 0 && <PublisherAppsTable apps={apps} />}
      {recentWithdrawals.length > 0 && (
        <RecentWithdrawalsTable rows={recentWithdrawals} />
      )}
    </div>
  );
}

function PublisherAppsTable({ apps }: { apps: AppSummary[] }) {
  return (
    <Card>
      <SectionLabel>Apps ({apps.length})</SectionLabel>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 text-right font-medium">Installs</th>
              <th className="pb-2 text-right font-medium">Live</th>
              <th className="pb-2 text-right font-medium">Live Time</th>
              <th className="pb-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-nurev-border">
            {apps.map(a => (
              <tr key={a._id}>
                <td className="py-2">
                  <p className="font-medium text-nurev-text">{a.name || '—'}</p>
                  <p className="font-mono text-[10px] text-nurev-muted">{a.appKey}</p>
                </td>
                <td className="py-2 text-right font-mono text-nurev-text">
                  {fmtNum(a.installs)}
                </td>
                <td className="py-2 text-right font-mono text-nurev-text">
                  {fmtNum(a.live)}
                </td>
                <td className="py-2 text-right text-nurev-text">
                  {fmtDuration(a.liveTime)}
                </td>
                <td className="py-2 text-xs text-nurev-muted">
                  {fmtDate(a.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RecentWithdrawalsTable({ rows }: { rows: WithdrawalSummary[] }) {
  return (
    <Card>
      <SectionLabel>Recent Withdrawals</SectionLabel>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
              <th className="pb-2 font-medium">Date</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 text-right font-medium">From</th>
              <th className="pb-2 text-right font-medium">To</th>
              <th className="pb-2 font-medium">Provider</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-nurev-border">
            {rows.map(w => (
              <tr key={w._id}>
                <td className="py-2 text-xs text-nurev-muted">
                  {fmtDate(w.createdAt)}
                </td>
                <td className="py-2">
                  <Badge value={w.status} />
                </td>
                <td className="py-2 text-right font-mono text-xs text-nurev-text">
                  {fmtNum(w.fromAmount)} {w.fromCurrency}
                </td>
                <td className="py-2 text-right font-mono text-xs text-nurev-text">
                  {w.toAmount ? `${w.toAmount} ${w.outCurrency?.toUpperCase()}` : '—'}
                </td>
                <td className="py-2 text-xs text-nurev-muted">
                  {w.exchangeProvider || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

type Tab = 'overview' | 'search';

export function CustomerServiceScreen() {
  const [tab, setTab] = useState<Tab>('overview');
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [publisherId, setPublisherId] = useState<string | null>(null);

  const selectDevice = (id: string) => {
    setDeviceId(id);
    setPublisherId(null);
  };
  const selectPublisher = (id: string) => {
    setPublisherId(id);
    setDeviceId(null);
  };
  const back = () => {
    setDeviceId(null);
    setPublisherId(null);
    setTab('search');
  };

  const inDetail = deviceId !== null || publisherId !== null;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Customer Service
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            User lookup, device management, platform overview. Balance changes are
            audit-logged.
          </p>
        </div>
        <a
          href="https://app.nurev.io/customer-service"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      {!inDetail && (
        <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
          {(['overview', 'search'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-md px-4 py-2 text-xs font-medium capitalize transition ${
                tab === t
                  ? 'bg-amber-500 text-nurev-bg'
                  : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {!inDetail && tab === 'overview' && <OverviewTab />}
      {!inDetail && tab === 'search' && (
        <SearchTab onSelectDevice={selectDevice} onSelectPublisher={selectPublisher} />
      )}

      {deviceId && (
        <DeviceDetailPanel
          deviceId={deviceId}
          onBack={back}
          onSelectPublisher={selectPublisher}
        />
      )}
      {publisherId && (
        <PublisherDetailPanel publisherId={publisherId} onBack={back} />
      )}
    </div>
  );
}

// Helper exports — leave Summary types alone, but `PublisherSummary` and
// `DeviceSummary` need to be re-exported so the screen's internal table
// rows can reference them without cross-importing from @/api directly.
export type { DeviceSummary, PublisherSummary };
